import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api, { downloadBlob } from '../services/api';
import { TASK_STATUSES, PriorityPill, fmtDate, TaskModal } from './TasksCommon';
import ProjectGantt from './ProjectGantt';
import ProjectMatrix from './ProjectMatrix';
import ProjectFlow from './ProjectFlow';
import ProjectWbs from './ProjectWbs';
import DispatchModal from './DispatchModal';
import './Tasks.css';

export default function ProjectBoard() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem('project_view') || 'kanban');
  const [showDispatch, setShowDispatch] = useState(false);
  const [showNewChild, setShowNewChild] = useState(null); // {parentId, kind}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getProjectBoard(id);
      setProject(res.data.project);
      setColumns(res.data.columns);
    } catch { /* ignore */ }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [uRes, pRes] = await Promise.all([api.getUsers(), api.getProjects()]);
        const usersData = uRes.data.results || uRes.data;
        setUsers(usersData.map((u) => ({
          id: u.id,
          username: u.username,
          full_name: [u.last_name, u.first_name].filter(Boolean).join(' '),
          position: u.position,
        })));
        setProjects(pRes.data.results || pRes.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Оптимистичное обновление
    const newCols = columns.map((c) => ({ ...c, tasks: [...c.tasks] }));
    const srcCol = newCols.find((c) => c.status === source.droppableId);
    const dstCol = newCols.find((c) => c.status === destination.droppableId);
    const [moved] = srcCol.tasks.splice(source.index, 1);
    moved.status = dstCol.status;
    dstCol.tasks.splice(destination.index, 0, moved);
    setColumns(newCols);

    // На бэк
    try {
      await api.changeTaskStatusV2(draggableId, dstCol.status);
    } catch (e) {
      alert('Не удалось изменить статус: ' + (e.response?.data?.detail || e.message));
      load(); // откат
    }
  };

  if (loading) return <div className="tasks-page">Загрузка…</div>;
  if (!project) return <div className="tasks-page"><div className="tasks-empty">Проект не найден</div></div>;

  return (
    <div className="tasks-page">
      <div style={{ marginBottom: 8 }}>
        <Link to="/tasks/projects" style={{ color: '#6b7280', textDecoration: 'none', fontSize: 14 }}>← Все проекты</Link>
      </div>
      <h1>{project.title}</h1>
      <div style={{ color: '#6b7280', marginBottom: 16 }}>
        {project.team_name} · прогресс {project.progress}%
      </div>

      <div className="tasks-toolbar">
        <div className="tasks-view-switcher">
          <button
            className={view === 'kanban' ? 'active' : ''}
            onClick={() => { setView('kanban'); localStorage.setItem('project_view', 'kanban'); }}
          >
            Kanban
          </button>
          <button
            className={view === 'gantt' ? 'active' : ''}
            onClick={() => { setView('gantt'); localStorage.setItem('project_view', 'gantt'); }}
          >
            Gantt
          </button>
          <button
            className={view === 'matrix' ? 'active' : ''}
            onClick={() => { setView('matrix'); localStorage.setItem('project_view', 'matrix'); }}
          >
            Матрица
          </button>
          <button
            className={view === 'flow' ? 'active' : ''}
            onClick={() => { setView('flow'); localStorage.setItem('project_view', 'flow'); }}
          >
            Ход согласования
          </button>
          <button
            className={view === 'wbs' ? 'active' : ''}
            onClick={() => { setView('wbs'); localStorage.setItem('project_view', 'wbs'); }}
          >
            WBS план
          </button>
        </div>
        <button className="tasks-btn primary" onClick={() => setShowNew(true)}>+ Новая задача</button>
        <button className="tasks-btn" onClick={() => setShowDispatch(true)}>📤 Разослать по отделам</button>
      </div>

      {view === 'matrix' && (
        <ProjectMatrix
          projectId={project.id}
          onOpenTask={(id) => setOpenTaskId(id)}
          onDispatch={() => setShowDispatch(true)}
        />
      )}

      {view === 'flow' && (
        <ProjectFlow
          projectId={project.id}
          onOpenTask={(id) => setOpenTaskId(id)}
        />
      )}

      {view === 'wbs' && (
        <>
          <div className="tasks-toolbar" style={{ marginBottom: 8 }}>
            <button
              className="tasks-btn primary"
              onClick={() => setShowNewChild({ parentId: null, kind: 'phase' })}
            >
              + Фаза (верхний уровень)
            </button>
            <button
              className="tasks-btn"
              onClick={() => setShowNewChild({ parentId: null, kind: 'task' })}
            >
              + Задача (верхний уровень)
            </button>
            <button
              className="tasks-btn"
              onClick={() => setShowNewChild({ parentId: null, kind: 'milestone' })}
            >
              ◆ + Веха
            </button>
            <label className="tasks-btn" style={{ marginLeft: 'auto', cursor: 'pointer' }}>
              📥 Импорт MS Project (.xml)
              <input
                type="file"
                accept=".xml"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const res = await api.importMsProjectXml(project.id, f);
                    alert(`Импортировано задач: ${res.data.imported}`);
                    load();
                  } catch (err) {
                    alert('Не удалось импортировать: ' + (err.response?.data?.detail || err.message));
                  }
                  e.target.value = '';
                }}
              />
            </label>
            <button
              className="tasks-btn"
              onClick={async () => {
                const name = window.prompt('Название базового плана:', `План от ${new Date().toLocaleDateString('ru-RU')}`);
                if (!name) return;
                try {
                  const res = await api.createProjectBaseline(project.id, { name });
                  alert(`Базовый план «${name}» зафиксирован. Захвачено задач: ${res.data.tasks_captured}.\nТеперь в WBS видно план (тонкая серая полоса) и факт (обычный бар).`);
                  load();
                } catch (e) {
                  alert('Не удалось: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              📌 Зафиксировать план (Baseline)
            </button>
            <button
              className="tasks-btn"
              onClick={async () => {
                try {
                  const res = await api.downloadWbsPdf(project.id);
                  downloadBlob(res, `WBS-${project.id}.pdf`);
                } catch (e) {
                  alert('Не удалось скачать: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              📄 PDF (лист как MS Project)
            </button>
            <button
              className="tasks-btn"
              onClick={async () => {
                try {
                  const res = await api.downloadWbsXml(project.id);
                  downloadBlob(res, `MSProject-${project.id}.xml`);
                } catch (e) {
                  alert('Не удалось скачать: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              📤 Экспорт MS Project (.xml)
            </button>
            <button
              className="tasks-btn"
              onClick={async () => {
                try {
                  const res = await api.downloadProjectReport(project.id);
                  downloadBlob(res, `Otchyot-Proekt-${project.id}.docx`);
                } catch (e) {
                  alert('Не удалось скачать: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              📊 Отчёт по проекту (Word)
            </button>
          </div>
          <ProjectWbs
            projectId={project.id}
            onOpenTask={(id) => setOpenTaskId(id)}
            onAddChild={(parentId, kind) => setShowNewChild({ parentId, kind })}
          />
        </>
      )}

      {showNewChild && (
        <TaskModal
          taskId={null}
          projectId={project.id}
          parentId={showNewChild.parentId}
          initialIsMilestone={showNewChild.kind === 'milestone'}
          projects={projects}
          users={users}
          onClose={() => setShowNewChild(null)}
          onSaved={() => { setShowNewChild(null); load(); }}
        />
      )}

      {view === 'gantt' && (
        <ProjectGantt columns={columns} project={project} onTaskClick={(id) => setOpenTaskId(id)} />
      )}

      {showDispatch && (
        <DispatchModal
          projectId={project.id}
          projectTitle={project.title}
          onClose={() => setShowDispatch(false)}
          onDispatched={() => load()}
        />
      )}

      {view === 'kanban' && (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="kanban-board">
          {columns.map((col) => (
            <Droppable droppableId={col.status} key={col.status}>
              {(provided, snapshot) => (
                <div
                  className={`kanban-column ${snapshot.isDraggingOver ? 'drop-over' : ''}`}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <h4>{col.status_display} <span className="count">({col.tasks.length})</span></h4>
                  {col.tasks.map((task, idx) => (
                    <Draggable draggableId={String(task.id)} index={idx} key={task.id}>
                      {(prov) => (
                        <div
                          className={`kanban-card ${task.is_overdue ? 'overdue' : ''}`}
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          {...prov.dragHandleProps}
                          onClick={() => setOpenTaskId(task.id)}
                        >
                          <div className="title">{task.title}</div>
                          <div className="meta">
                            <span className="assignee">
                              {task.assignee_detail?.full_name || task.assignee_detail?.username || '—'}
                            </span>
                            <span className="due">{fmtDate(task.due_date)}</span>
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <PriorityPill value={task.priority} />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
      )}

      {openTaskId && (
        <TaskModal
          taskId={openTaskId}
          users={users}
          projects={projects}
          onClose={() => setOpenTaskId(null)}
          onSaved={() => { setOpenTaskId(null); load(); }}
        />
      )}

      {showNew && (
        <TaskModal
          taskId={null}
          projectId={project.id}
          projects={projects}
          users={users}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}
